import { SelectableHeatMapFeature } from 'app/map/services/heatmap/types/SelectableHeatMapFeature';
import { Cesium } from 'app/cesium';
import { DataWithLocationArray } from 'app/shared/collections/time/ims_time_collection.factory';

/**
 * Just a helper data structure. Used when parsing the selectable feature id string that we put
 * on cesium objects.
 */
interface SelectableFeatureId {
    // AbstractGeoDataMap id. Used to pull the geo ma[p from the GeoDataService
    geoMapId: number;
    // Choropleth ID. Can also be the stat ID depending on AbstractGeoDataMap implementation
    featureId: number;
}

/**
 * Turns the selectable feature ID into a string so we can attach it to a cesium
 * clickable object
 *
 * @param {number} geoMapId
 * @param {SelectableHeatMapFeature} f
 * @returns {string}
 */
export function formatSelectableFeatureId(geoMapId: number, f: SelectableHeatMapFeature): string {
    return `${geoMapId}-${f.id}`;
}

/**
 * Changes the ID string back into a data structure.
 *
 * @param {string} selectableFeatureIdString
 * @returns {SelectableFeatureId}
 */
export function parseSelectableFeatureId(selectableFeatureIdString: string): SelectableFeatureId {
    const splitIds = selectableFeatureIdString.split('-').map(Number);
    return {
        geoMapId: splitIds[0],
        featureId: splitIds[1],
    };
}

/**
 * The abstract geo data map class. Everything we need to generate, display and select drawn
 * data points from the map.
 */
export abstract class AbstractGeoDataMap {
    protected readonly _id: number;
    protected _dataByLocation: DataWithLocationArray;

    protected constructor(id: number, dataByLocation: DataWithLocationArray) {
        this._id = id;
        this._dataByLocation = dataByLocation;
    }

    get id(): number {
        return this._id;
    }

    /**
     * Can be null
     * @returns {string}
     */
    public abstract get statType(): string;

    /**
     *
     * @param {number} latitude
     * @param {number} longitude
     * @param {number} maxResults
     * @returns {any}
     */
    public abstract lookupSelectableFeatures(
        latitude: number,
        longitude: number,
        maxResults?: number,
    ): SelectableHeatMapFeature[];

    /**
     * Get the feature by featureId. See the SelectableFeatureId data structure
     * @param featureId
     * @returns {SelectableHeatMapFeature}
     */
    public abstract getSelectableFeature(featureId): SelectableHeatMapFeature;

    /**
     * Draw the layer onto the cesium map
     * @param {Cesium.Viewer} viewer
     */
    public abstract drawOnViewer(viewer: Cesium.Viewer);

    /**
     * Remove the layer from the cesium map
     */
    public abstract delete(): void;
}
